import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Table } from 'reactstrap';
import Button from 'components/App/Button';
import Loading from 'components/App/Loading';
import AppBadge from 'components/App/AppBadge';
import ModalImagePreview from 'components/Modals/ModalImagePreview';
import {
  LOCATIONS,
  TCXH_FEEDBACK_STATUS,
  TCXH_FEEDBACK_STATUS_LIST,
} from 'constants/index';

import styles from './DataTableTcxhFeedbackList.module.scss';

const getStatusBadgeColor = (status) => {
  switch (status) {
    case TCXH_FEEDBACK_STATUS.APPROVED:
    case TCXH_FEEDBACK_STATUS.RESOLVED:
    case 'APPROVED':
    case 'RESOLVED':
      return 'success';
    case TCXH_FEEDBACK_STATUS.PENDING:
    case 'PENDING':
      return 'caution';
    case TCXH_FEEDBACK_STATUS.REJECTED:
    case 'REJECTED':
      return 'error';
    default:
      return 'info';
  }
};

const getStatusLabel = (status) => {
  const found = TCXH_FEEDBACK_STATUS_LIST.find((item) => item.value === status);
  return found ? found.label : status || '-';
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

function DataTableTcxhFeedbackList(props) {
  const {
    isLoading,
    feedbacks,
    onOpenDetail,
    page,
    limit,
  } = props;
  const history = useHistory();
  const [preview, setPreview] = useState({
    visible: false,
    images: [],
    current: 0,
  });

  const startIndex = ((page || 1) - 1) * (limit || 20);

  const handleOpenDetail = (feedback) => {
    if (onOpenDetail) {
      onOpenDetail(feedback);
    } else {
      history.push(`${LOCATIONS.MANAGER_TCXH_FEEDBACK}/${feedback._id || feedback.id}`);
    }
  };

  const handlePreviewImages = (e, images, index = 0) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setPreview({
      visible: true,
      images: images || [],
      current: index,
    });
  };

  return (
    <div className={styles.Component}>
      <Loading loading={isLoading} loaderOnPage />
      <Table responsive className="Table">
        <thead className="Table__Header">
          <tr className="Table__Header__Row">
            <th
              className="Table__Header__Row__Content text-center"
              style={{ minWidth: '80px', width: '80px' }}
            >
              STT
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '150px' }}
            >
              Ngày tạo
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '240px' }}
            >
              Tên tổ chức xã hội
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '180px' }}
            >
              Số quyết định thành lập
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '200px' }}
            >
              Địa chỉ email
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '280px' }}
            >
              Nội dung liên hệ
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '140px' }}
            >
              Ảnh đính kèm
            </th>

            <th
              className="Table__Header__Row__Content"
              style={{ minWidth: '140px' }}
            >
              Trạng thái
            </th>

            <th
              className="Table__Header__Row__Content text-center"
              style={{ minWidth: '120px' }}
            >
              Thao tác
            </th>
          </tr>
        </thead>

        <tbody className="Table__Body">
          {feedbacks && feedbacks.length > 0 ? (
            feedbacks.map((item, index) => (
              <tr className="Table__Body__Row" key={item._id || item.id}>
                {/* 1. STT */}
                <td className="Table__Body__Row__Content text-center">
                  {startIndex + index + 1}
                </td>

                {/* 2. Ngày tạo */}
                <td className="Table__Body__Row__Content">
                  {formatDate(item.createdAt)}
                </td>

                {/* 3. Tên tổ chức xã hội */}
                <td className="Table__Body__Row__Content">
                  <span className="Content__Organization">
                    {item.organizationName || item.organization || '-'}
                  </span>
                </td>

                {/* 4. Số quyết định thành lập */}
                <td className="Table__Body__Row__Content">
                  <span className="Content__DecisionNumber">
                    {item.decisionNumber || item.establishmentDecisionNumber || '-'}
                  </span>
                </td>

                {/* 5. Địa chỉ email */}
                <td className="Table__Body__Row__Content">
                  <span className="Content__Email">{item.email || '-'}</span>
                </td>

                {/* 6. Nội dung liên hệ */}
                <td className="Table__Body__Row__Content">
                  <p className="Content__Text" title={item.content}>
                    {item.content || '-'}
                  </p>
                </td>

                {/* 7. Ảnh đính kèm */}
                <td className="Table__Body__Row__Content">
                  {item.images && item.images.length > 0 ? (
                    <div className="ImageThumbnails">
                      {item.images.slice(0, 2).map((imgUrl, idx) => (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={idx}
                          className="ThumbWrapper"
                          onClick={(e) => handlePreviewImages(e, item.images, idx)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handlePreviewImages(e, item.images, idx);
                          }}
                        >
                          <img src={imgUrl} alt={`Ảnh đính kèm ${idx + 1}`} />
                        </div>
                      ))}
                      {item.images.length > 2 && (
                        <div
                          className="MoreImagesBadge"
                          onClick={(e) => handlePreviewImages(e, item.images, 2)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handlePreviewImages(e, item.images, 2);
                          }}
                        >
                          {`+${item.images.length - 2}`}
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="NoImageText">Không có ảnh</span>
                  )}
                </td>

                {/* 8. Trạng thái */}
                <td className="Table__Body__Row__Content">
                  <AppBadge
                    className="Badge"
                    color={getStatusBadgeColor(item.status)}
                  >
                    {getStatusLabel(item.status)}
                  </AppBadge>
                </td>

                {/* 9. Thao tác */}
                <td className="Table__Body__Row__Content text-center">
                  <Button
                    outline
                    classes="Action__Button Action__Button__Link"
                    onClick={() => handleOpenDetail(item)}
                  >
                    Xem chi tiết
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr className="Table__Body__Row">
              <td
                colSpan="9"
                className="Table__Body__Row__Content text-center"
              >
                <div className="EmptyData">
                  <i className="fas fa-inbox fa-3x" />
                  <p>Không tìm thấy phản ánh nào của tổ chức xã hội</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Image Preview Modal */}
      <ModalImagePreview
        isOpen={preview.visible}
        images={preview.images}
        initialIndex={preview.current}
        onClose={() => setPreview((prev) => ({ ...prev, visible: false }))}
      />
    </div>
  );
}

DataTableTcxhFeedbackList.propTypes = {
  isLoading: PropTypes.bool,
  feedbacks: PropTypes.arrayOf(PropTypes.shape({})),
  onOpenDetail: PropTypes.func,
  page: PropTypes.number,
  limit: PropTypes.number,
};

DataTableTcxhFeedbackList.defaultProps = {
  isLoading: false,
  feedbacks: [],
  onOpenDetail: null,
  page: 1,
  limit: 20,
};

export default DataTableTcxhFeedbackList;
